import React, {FC} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../../constants/typography';
import {HighlightBrand, HighlightedBrandType} from './types';

interface Props {
  brands: Array<HighlightBrand>;
  highlightedBrandsType?: HighlightedBrandType;
  onBrandClick: (brandId: string, brandSlug?: string) => void;
  label: string;
  activeBrandIds: Array<string>;
  favoriteBrandsActive?: boolean;
  excludedHighlightedBrands?: Array<string>;
  className?: string;
  dataTestId?: string;

  Wrapper?: React.ComponentType<React.PropsWithChildren>;
}

export const HighlightedBrands: FC<Props> = ({
  brands,
  highlightedBrandsType,
  onBrandClick,
  label,
  activeBrandIds,
  favoriteBrandsActive,
  excludedHighlightedBrands,
  className,
  dataTestId = '',
  Wrapper = React.Fragment,
}) => {
  return (
    <div data-testid={dataTestId} className={className}>
      <HighlightedBrandsLabel>{label}</HighlightedBrandsLabel>
      <Wrapper>
        {brands.map(brand => {
          const active = highlightedBrandsType
            ? {
                performingBrands: activeBrandIds.includes(brand.id),
                personalizedBrands: Boolean(
                  favoriteBrandsActive &&
                    !excludedHighlightedBrands?.includes(brand.id),
                ),
              }[highlightedBrandsType]
            : false;

          return (
            <BrandTile
              data-testid={`customerBrandTile_${brand.id}${
                active ? '_active' : ''
              }`}
              isActive={active}
              onClick={() => {
                onBrandClick(brand.id, brand.slug);
              }}
              key={`brand-tile-${brand.id}`}
            >
              {brand.img ? (
                <BrandTileLogo
                  alt={brand.name}
                  data-testid="customerBrandTileLogo"
                  loading="lazy"
                  src={brand.img}
                />
              ) : (
                <BrandTileLogoAlt>
                  <BrandTileLogoAltLabel>{brand.name}</BrandTileLogoAltLabel>
                </BrandTileLogoAlt>
              )}
            </BrandTile>
          );
        })}
      </Wrapper>
    </div>
  );
};

const BrandTileLogoAltLabel = styled.label`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.Black};
  text-align: center;
  white-space: pre-wrap;
  overflow: hidden;
`;

const BrandTileLogoAlt = styled.div`
  box-sizing: border-box;
  padding: 0.5rem;
  width: 110px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

// ❌ 🚨 Why are we using an styled image in here and not UI-Image?
// eslint-disable-next-line local-rules/prefer-image-component-over-html-image-tag
const BrandTileLogo = styled.img`
  padding: 5px;
  object-fit: contain;
  width: 80px;
  height: 30px;
`;

const BrandTile = styled.button<{isActive: boolean}>`
  border-radius: 3px;
  white-space: nowrap;
  border: ${({isActive}) =>
    isActive ? `1px solid ${COLORS.Black}` : `1px solid ${COLORS.Grey3}`};
  background: ${COLORS.Grey4};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 5px;
  margin-bottom: 5px;

  &:focus {
    outline: none;
  }
`;

const HighlightedBrandsLabel = styled.div`
  color: ${COLORS.Black};
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.bold};
  margin-bottom: 0.75rem;
`;
